<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    modelValue: string | boolean | Array<string | boolean>
    options: Array<{ label: string; value: string | boolean; description: string }>
    multiple?: boolean
    disabled?: boolean
    label?: string
    description?: string
    property?: string
    v$?: any
    error?: string
    columns?: number
  }>(),
  {
    multiple: false,
    disabled: false,
    label: '',
    description: '',
    v$: {},
    columns: 0,
  },
)

const emit = defineEmits(['update:modelValue'])

// Generate unique component ID to prevent DOM conflicts
const componentId = 'multi-select-cards-' + Math.random().toString(36).substring(2, 10)

function isSelected(value: string | boolean) {
  if (props.multiple) {
    return Array.isArray(props.modelValue) && props.modelValue.includes(value)
  } else {
    return props.modelValue === value
  }
}

function toggleSelection(value: string | boolean) {
  if (props.multiple) {
    const selected = Array.isArray(props.modelValue) ? [...props.modelValue] : []
    const index = selected.indexOf(value)

    if (index > -1) {
      selected.splice(index, 1)
    } else {
      selected.push(value)
    }

    emit('update:modelValue', selected)
  } else {
    emit('update:modelValue', value)
  }
}

const error = computed(() => props.error || props.v$[props.property]?.$errors[0]?.$message)

const gridClass = computed(() => {
  if (!props.columns) return 'flex w-full flex-col'
  const cols: Record<number, string> = {
    2: 'grid grid-cols-2',
    3: 'grid grid-cols-3',
    4: 'grid grid-cols-4',
  }
  return cols[props.columns] || 'flex w-full flex-col'
})
</script>

<template>
  <div :id="componentId" class="flex w-full flex-col">
    <!-- Label -->
    <label v-if="label" class="mb-2 block text-sm font-medium text-gray-700">
      {{ label }}
    </label>
    <p v-if="description" class="mb-2 text-sm text-gray-500">{{ description }}</p>

    <!-- Card buttons -->
    <div class="gap-2" :class="[gridClass, { 'pointer-events-none opacity-50': disabled }]">
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        @click="toggleSelection(option.value)"
        :disabled="disabled"
        class="rounded-md border-none text-left shadow-sm ring-1 transition-shadow active:translate-y-px active:shadow-sm"
        :class="[
          columns ? 'px-4 py-3 text-center' : 'flex w-full items-center justify-between px-6 py-5',
          isSelected(option.value)
            ? 'bg-[#009192]/[0.08] text-gray-900 ring-[#009192] hover:shadow-md'
            : 'bg-white text-gray-700 ring-gray-700/20 hover:shadow-md hover:ring-gray-700/30',
        ]"
      >
        <div>
          <div class="text-sm font-semibold leading-snug">{{ option.label }}</div>
          <div v-if="option.description" class="mt-0.5 text-sm text-gray-500">{{ option.description }}</div>
        </div>
      </button>
    </div>
    <template v-if="error">
      <p class="mt-1 text-sm text-red-500">{{ error }}</p>
    </template>
  </div>
</template>
